import { useAppConfig } from '#/config/AppConfigContext';
import { ClientOnly } from '#/containers/ClientOnly';
import { useUser } from '#/contexts/UserEnvironmentContext';
import type { CmsApiPost } from '#/modules/cms/api/types/CmsApiDocument';
import { CmsAuthorCard } from '#/modules/cms/components/AuthorCard/CmsAuthorCard';
import { CmsBodyHolder } from '#/modules/cms/components/BodyHolder/CmsBodyHolder';
import { CmsBodyHolderWidth } from '#/modules/cms/components/BodyHolder/CmsBodyHolderWidth';
import { CmsTagList } from '#/modules/cms/components/TagList/CmsTagList';
import { getCmsApiDocumentHomeMeta } from '#/modules/cms/helpers/getCmsApiDocumentHomeMeta';
import { dynamic } from '#/utils/dynamic';

type Props = {
  content: CmsApiPost;
};

const DynamicOpenWebWidget = dynamic(
  () =>
    import('#/modules/cms/components/Comments/OpenWebWidget').then(
      (module) => ({
        default: module.OpenWebWidget,
      }),
    ),
  { strategy: 'client-only' },
);

const DynamicCmsReactions = dynamic(
  () =>
    import('#/modules/cms/components/Reactions/CmsReactions').then(
      (module) => ({
        default: module.CmsReactions,
      }),
    ),
  { strategy: 'client-only' },
);

export function CmsPostLayoutBelowBlocks({ content }: Props) {
  const { user } = useUser();
  const { homePageUrl } = getCmsApiDocumentHomeMeta({ content });

  const { appConfig } = useAppConfig();
  const openwebEnabled = Boolean(appConfig.openwebSpotId);

  return (
    <>
      {content.tags.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <CmsBodyHolder holderWidth={CmsBodyHolderWidth.NARROW_LAYOUT}>
            <CmsTagList
              homePageUrl={content.subsite.homePage?.url}
              tags={content.tags}
            />
          </CmsBodyHolder>
        </div>
      )}

      {content.author && (
        <CmsBodyHolder holderWidth={CmsBodyHolderWidth.NARROW_LAYOUT}>
          <CmsAuthorCard
            author={content.author}
            homePageUrl={homePageUrl}
            postedDate={content.postedDate}
            showDate={content.showDate}
          />
        </CmsBodyHolder>
      )}

      {content.hasReactions || content.hasSocialSharing ? (
        <CmsBodyHolder holderWidth={CmsBodyHolderWidth.NARROW_LAYOUT}>
          <ClientOnly>
            <DynamicCmsReactions content={content} user={user} />
          </ClientOnly>
        </CmsBodyHolder>
      ) : null}

      {content.enableComments && openwebEnabled && (
        <CmsBodyHolder holderWidth={CmsBodyHolderWidth.NARROW_LAYOUT}>
          <ClientOnly>
            <DynamicOpenWebWidget
              postId={content.id}
              relativePath={content.url}
              userId={user ? user.id : undefined}
              hasIdealistProfileFeature={
                user ? Boolean(user.hasIdealistProfileFeature) : false
              }
              hasPublicProfile={user ? user.hasPublishedIdealistProfile : false}
            />
          </ClientOnly>
        </CmsBodyHolder>
      )}
    </>
  );
}
